import { faBell, faEnvelope, faMessage } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSession } from 'src/contexts/SessionContext';

import { Box } from './Box';
import { Icon } from './Icon';
import { ToggleButton } from './ToggleButton';
import { ToggleButtonGroup } from './ToggleButtonGroup';
import { Tooltip } from './Tooltip';
import { Typography } from './Typography';

type DeliveryMethod = 'in-app' | 'email' | 'sms';

interface DeliveryMethodTogglesProps {
  value: DeliveryMethod[];
  onChange: (value: DeliveryMethod[]) => void;
}

export const DeliveryMethodToggles: React.FC<DeliveryMethodTogglesProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'deliveryMethod' });
  const { session } = useSession();
  const smsDisabled = !session.smsAvailable;

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: DeliveryMethod[]
  ) => {
    // Ensure at least one delivery method is selected
    if (newValue.length > 0) {
      // Filter out 'sms' if SMS is disabled
      const filteredValue = smsDisabled
        ? newValue.filter((v) => v !== 'sms')
        : newValue;
      if (filteredValue.length > 0) {
        onChange(filteredValue);
      }
    }
  };

  // Filter out 'sms' from value if SMS is disabled
  const displayValue = smsDisabled ? value.filter((v) => v !== 'sms') : value;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('title')}
      </Typography>
      <ToggleButtonGroup
        value={displayValue}
        onChange={handleChange}
        aria-label={t('ariaLabel')}
        exclusive={false}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          '& .MuiToggleButton-root': {
            flex: 1,
            p: 0.75,
            minWidth: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
            '&.Mui-disabled': {
              opacity: 0.4,
              cursor: 'not-allowed',
            },
          },
        }}
      >
        <Tooltip
          title={t('inApp')}
          arrow
          PopperProps={{ sx: { zIndex: 1500 } }}
        >
          <Box component="span">
            <ToggleButton value="in-app" aria-label={t('inApp')}>
              <Icon
                icon={faBell}
                size="small"
              />
            </ToggleButton>
          </Box>
        </Tooltip>
        <Tooltip
          title={t('email')}
          arrow
          PopperProps={{ sx: { zIndex: 1500 } }}
        >
          <Box component="span">
            <ToggleButton value="email" aria-label={t('email')}>
              <Icon
                icon={faEnvelope}
                size="small"
              />
            </ToggleButton>
          </Box>
        </Tooltip>
        <Tooltip
          title={smsDisabled ? t('smsDisabled') : t('sms')}
          arrow
          PopperProps={{ sx: { zIndex: 1500 } }}
        >
          <Box component="span">
            <ToggleButton
              value="sms"
              aria-label={t('sms')}
              disabled={smsDisabled}
            >
              <Icon
                icon={faMessage}
                size="small"
              />
            </ToggleButton>
          </Box>
        </Tooltip>
      </ToggleButtonGroup>
    </Box>
  );
};
